import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as yup from 'yup'
import {
  faList,
} from '@fortawesome/pro-regular-svg-icons';
import DataTableSectionWithUpload from 'src/components/era-package/forms/DataTableSectionWithUpload';

import AbiEntrySummaryApiUrls from "../../../apiUrls/AbiEntrySummaryApiUrls";
import Spinner from "../../../components/era-package/era/Spinner";
import {pga} from "../fields/pga-flag-code";

export const usePgaFlagForm = (data, setData) => {
  const key = 'pga';
  const fileFormat = ['.xlsx'];
  const [loading, setLoading] = useState(true);
  const [filesUnderProcessing, setFilesUnderProcessing] = useState("")

  const handleUploadSuccess = useCallback(() => {
    console.log("Custom post upload logic for duty calculation");
  }, []);

  const fetchData = async () => {
    const response = await fetch(`${AbiEntrySummaryApiUrls.GET_PGA_FLAG_CODE}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
      },
    });
    const json = await response.json();
    return json.data;
  };

  const handleApiCall = useCallback(async (file, formData, formMethods) => {

    const apiFormData = new FormData();
    apiFormData.append('file', file);

    try {
      const response = await fetch(AbiEntrySummaryApiUrls.UPLOAD_PGA_FLAG_CODE, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: apiFormData
      });

      if (!response.ok) {
        throw new Error('Error uploading file');
      }

      const result = await response.json();
      console.log('File uploaded successfully:', result);
      setFilesUnderProcessing(file.name);
    } catch (error) {
      throw new Error('Error uploading file');
    }
  }, []);

        const handlePaginationData = useCallback(async (pageIndex, pageSize, globalFilter) => {
    try {
      const response = await fetch(AbiEntrySummaryApiUrls.LIST_PGA_FLAG_CODE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify({
          page: pageIndex,
          size: pageSize,
          search: globalFilter ?? '',
          tariffFlagCode: '',
          agencyCode: '',
          programCode: '',
          requiredFlag: '',
        }),
      })
      if (!response.ok) {
        console.log('Error retrieving data..');
      }
      const result = await response.json();
      return result?.data;
    } catch (error) {
      console.log('Error retrieving data');
    }
  }, [])

  const handleRefresh = useCallback(async (rhf) => {
    try {
      setLoading(true);
      const result = await handlePaginationData(0, 10, '');
      const items = result?.content ?? [];
      setData(items);
      setLoading(false);
      rhf.setValue('pga', items)
      return result;
    } catch (error) {
      console.error('Error refreshing line items:', error);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setData, handlePaginationData]);

  const handleDeleteRow = useCallback(async (id) => {
    try {

      const response = await fetch(AbiEntrySummaryApiUrls.DELETE_PGA_FLAG_CODE+`?id=${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
      })

      if (!response.ok) {
        console.log('Error deleting file');
      }
      const result = await response.json();
      console.log('File deleted successfully:', result);
      return result.data;
    } catch (error) {
      console.log('Error deleting file');
    }
  }, []);

  const onSaveData = async(data, rhf) => {
    try {
      const response = await fetch(AbiEntrySummaryApiUrls.EDIT_PGA_FLAG_CODE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        console.log('Error saving file');
        return false;
      }
      const result = await response.json();
      console.log('File save successfully:', result);
      handleRefresh(rhf)
      return true;
    } catch (error) {
      console.log('Error saving file');
      return false;
    }
  }

  const formSections = useMemo(() => [
    {
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table', name: `${key}`,
      tableComponent: DataTableSectionWithUpload,
      disableEdit: false,
      tableFields: pga,
      onUploadSuccess: handleUploadSuccess,
      handleApiCall: handleApiCall,
      handleRefresh: handleRefresh,
      handleDeleteRow: handleDeleteRow,
      filesUnderProcessing: filesUnderProcessing,
      disableAdd: true,
      handleSave: onSaveData,
      fileFormat: fileFormat,
      handlePaginationData: handlePaginationData
    }
  ], [handleApiCall, handleUploadSuccess, handleRefresh, handleDeleteRow, handlePaginationData]);

  return {
    name: key,
    formSections,
    schema: baseObj => yup.array().nullable().default([]),
    handleRefresh,
    loading
  }
}


const PgaFlagCodeFormComponent = () => {
  const rhf = useForm();
  const [data, setData] = useState([]);
  const { formSections, handleRefresh, loading } = usePgaFlagForm(data, setData);


  useEffect(() => {
    const loadData = async () => {
      await handleRefresh(rhf);
    };
    loadData();
  }, [handleRefresh]);


  const columns = [
    ...pga.map(field => {
      return {
        accessorKey: field.name,
        header: field.label,
      };
    }),
  ];

  const handleInput = data => {
    if (!!rhf)
      rhf.setValue(formSections[0].name, data)
    if (!!formSections[0].onTableDataChange)
      formSections[0].onTableDataChange(data)
  }

  return (
    <FormProvider {...rhf}> {}
      <div>
        {loading ? <div style={{ position: 'relative', height: '200px' }}>    <Spinner />  </div> : (
          formSections.map(section => {
            const TableComponent = section.tableComponent;
            return (
              <div key={section.title}>
                <h3>{section.title}</h3>
                <TableComponent
                  title={section.title}
                  fields={section.tableFields}
                  formFields={section.formFields}
                  columns={columns}
                  name={section.name}
                  fileFormat={section.fileFormat}
                  data={data}
                  handleApiCall={section.handleApiCall}
                                    handleRefresh={section.handleRefresh}
                  handleClearAll={section.handleClearAll}
                  handleEditSave={section.handleSave}
                  filesUnderProcessing={section.filesUnderProcessing}
                  onDataChange={handleInput}
                  handleDeleteRow={section.handleDeleteRow}
                  onUploadSuccess={section.onUploadSuccess}
                  uploadFormComponent={section.uploadFormComponent}
                  disableEdit={section.disableEdit}
                  disableAdd={true}
                  showCustomUploadBtn={true}
                  optionsMap={{}}
                  handlePaginationData={section.handlePaginationData}
                />
              </div>
            );
          })
        )}
      </div>
    </FormProvider>
  );
};

export default PgaFlagCodeFormComponent;
